import React from 'react';

import {Group} from '@pb/kit-component-group';
import {Tooltip} from '@pb/kit-component-popups';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {Button} from '../button';

import styles from './compound-button.module.styl';

import type {ButtonProps} from '../button';
import type {ReactNode} from 'react';


const classes = hashedClasses(styles);
const classNames = withCssModule(styles);

interface SecondaryButtonProps extends ButtonProps {
    /**
     * @deprecated It will be removed in 6 months 04-31-2026. Use renderSecondaryButton instead. Motivation: Button package should not depend on Tooltip package.
     * */
    tooltip?: string,
}

export type CompoundButtonProps = {
    /**
     * The divider between the primary and secondary buttons.
     * (use only with Type.FLAT variant)
     */
    divider?: ReactNode,
    /**
     * The primary button properties
     */
    primaryButton: ButtonProps,
    /**
     * Callback to render the secondary button
     */
    renderSecondaryButton?: (props: ButtonProps) => ReactNode,
    /**
     * The secondary button properties with tooltip text.
     * In the future, this would be TooltipProps
     */
    secondaryButton: SecondaryButtonProps,
    /**
     * The variant of the button
     */
    variant: ButtonProps['variant'],
};

/**
 * @description The `CompoundButton` component is a combination of two buttons, primary and secondary, with additional tooltip functionality for the secondary button.
 *
 * ## Features
 * - Provides two buttons with distinct roles, primary and secondary
 * - Secondary button includes a tooltip for additional information
 *
 * ## Accessibility
 * - The secondary button uses the `aria-label` attribute for accessibility, which is set to the tooltip text
 *
 * @see https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-button-compound-button--documentation
 **/

export const CompoundButton = (props: CompoundButtonProps) => {
    const {
        divider,
        primaryButton,
        renderSecondaryButton,
        secondaryButton,
        variant,
    } = props;

    const primaryButtonClasses = classNames('compound-button__primary', primaryButton?.className);
    const secondaryButtonClasses = classNames('compound-button__secondary', secondaryButton?.className);

    const {tooltip, ...otherSBProps} = secondaryButton;

    const secondaryButtonProps = {
        ...otherSBProps,
        variant,
        'aria-label': tooltip || secondaryButton['aria-label'],
        'className': secondaryButtonClasses,
    };

    const renderSecondaryButtonComponent = () => {
        const button = renderSecondaryButton
            ? renderSecondaryButton(secondaryButtonProps)
            : <Button {...secondaryButtonProps}/>;

        if (!tooltip) {
            return button;
        }

        return (
            <Tooltip
                anchor={({anchorRef, getAnchorProps}) => (
                    <div ref={anchorRef} {...getAnchorProps()}>
                        {button}
                    </div>
                )}>
                {tooltip}
            </Tooltip>
        );
    };

    return (
        <Group
            gap="none"
            className={classes['compound-button']}>
            <Button
                {...primaryButton}
                variant={variant}
                className={primaryButtonClasses}
            />
            {divider}
            {renderSecondaryButtonComponent()}
        </Group>
    );
};

CompoundButton.displayName = 'CompoundButton';
